/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Per-outlet delivery restrictions.
 *
 * Some sellers only deliver to part of the map — their city, chosen zones
 * ("Whitefield only") or a distance from the store. For those outlets we block
 * add-to-cart when the buyer's selected address sits outside the area and show
 * a notice on the storefront home page.
 *
 * The store's own setting comes from product-service
 * (`/v3/buyer/outlet/:outletId/service-area`, set in the CRM's "Where it
 * delivers"); the table below is only what an outlet reads until that answer
 * arrives, or if it can't be fetched. The server decides at cart and checkout
 * either way — this is the early warning.
 */
export interface ServiceArea {
  /** Lower-cased city names that are serviceable. */
  cities: string[];
  /**
   * Serviceable pincode ranges, inclusive `[from, to]`. Checked before the city
   * names because city text is unreliable — an Electronic City address comes
   * back as "Anekal", "Hebbagodi", "Bengaluru Urban", or just the locality
   * depending on where it was captured.
   */
  pincodeRanges: [number, number][];
  /** Shown in the home-page notice. */
  homeNotice: string;
  /** Shown in the home-page notice instead, while the selected address is outside the area. */
  outsideNotice: string;
  /** Shown when the buyer tries to add to cart from an unserviceable address. */
  blockMessage: string;
  /**
   * "Within N km" stores: the store's position and reach, in a straight line —
   * the same ruler the server uses. When set, the pincode and city lists are
   * not used. A store with no position on record serves everyone.
   */
  radius?: { center: LatLng | null; maxKm: number };
}

const BENGALURU_ONLY: ServiceArea = {
  cities: [
    "bengaluru", "bangalore", "bengaluru rural", "bengaluru urban",
    "bangalore rural", "bangalore urban", "anekal", "electronic city",
    "whitefield", "yelahanka", "kengeri", "hoskote", "devanahalli",
  ],
  pincodeRanges: [
    // Bengaluru Urban — every city pincode, 560001 (GPO) through 560300 (KIA).
    // Covers the whole Electronic City belt: 560100 Electronic City Phase 1 & 2 /
    // Neeladri Road / Doddathoguru, 560099 Hebbagodi / Bommasandra / Chandapura /
    // Huskur, 560105 Shikaripalya, 560068 Singasandra.
    [560001, 560300],
    // Bengaluru Rural north — Doddaballapur, Devanahalli, Nelamangala belt.
    [561101, 561229],
    // Anekal / Attibele / Sarjapur / Hoskote belt bordering Electronic City.
    [562100, 562135],
  ],
  homeNotice: "We currently deliver only within Bengaluru.",
  outsideNotice: "Selected address is outside Bengaluru — we don't deliver there yet.",
  blockMessage: "Sorry, we are not serving outside Bengaluru yet. Please select a Bengaluru address to continue.",
};

export const SERVICE_AREAS: Record<string, ServiceArea> = {
  BBLR00253000: BENGALURU_ONLY, // Jyoti Organic
  BBLR00249000: BENGALURU_ONLY, // Farm Fresh Bangalore
};

export const SELECTED_CITY_KEY = "selected_address_city";

/** The server's own sentence for a buyer outside a store's area. */
const DEFAULT_BLOCK_MESSAGE = "We are not serving in this location";

/** Straight-line km between two points. */
function haversineKm(a: LatLng, b: LatLng): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/* ── The store's own setting, from product-service ────────────────────────── */

/** outletId → the fetched area (null = delivers everywhere). Absent = not fetched yet. */
const remoteAreas = new Map<string, ServiceArea | null>();
const inflight = new Map<string, Promise<void>>();
let areasVersion = 0;

/** Dispatched on window whenever a store's area arrives, so hooks re-read. */
export const SERVICE_AREA_LOADED = "service_area_loaded";

/** Changes each time an area arrives — a stable snapshot for `useSyncExternalStore`. */
export function getServiceAreaVersion(): number {
  return areasVersion;
}

/** The product-service answer as a ServiceArea. Exported for tests. */
export function serviceAreaFromApi(d: any): ServiceArea | null {
  if (!d || d.mode === "everywhere" || !d.mode) return null;
  if (d.mode === "bengaluru") {
    // The store's own message wins; the standard server sentence reads worse
    // than the storefront's own Bengaluru line.
    const own = typeof d.blockMessage === "string" && d.blockMessage !== DEFAULT_BLOCK_MESSAGE ? d.blockMessage : null;
    return own ? { ...BENGALURU_ONLY, blockMessage: own } : BENGALURU_ONLY;
  }

  const label: string = d.label || "this area";
  const blockMessage: string = d.blockMessage || DEFAULT_BLOCK_MESSAGE;

  if (d.mode === "areas") {
    const pins = (Array.isArray(d.pincodes) ? d.pincodes : []).map(Number).filter((n: number) => Number.isInteger(n));
    return {
      cities: [],
      pincodeRanges: pins.map((n: number) => [n, n] as [number, number]),
      homeNotice: d.notice || `We currently deliver only in ${label}.`,
      outsideNotice: `Your address is outside ${label} — we don't deliver there yet.`,
      blockMessage,
    };
  }

  if (d.mode === "radius") {
    const maxKm = Number(d.maxKm);
    if (!Number.isFinite(maxKm) || maxKm <= 0) return null;
    const c = Array.isArray(d.coordinates) ? { lat: Number(d.coordinates[0]), lng: Number(d.coordinates[1]) } : null;
    return {
      cities: [],
      pincodeRanges: [],
      homeNotice: d.notice || `We currently deliver only within ${maxKm} km of the store.`,
      outsideNotice: `Your address is over ${maxKm} km away — we don't deliver there yet.`,
      blockMessage,
      radius: { center: c && Number.isFinite(c.lat) && Number.isFinite(c.lng) ? c : null, maxKm },
    };
  }

  return null;
}

/**
 * Fetches a store's area once per page load. Never rejects: on any failure the
 * outlet keeps reading the table above.
 */
export function loadServiceArea(outletId?: string | null): Promise<void> {
  if (!outletId || typeof window === "undefined") return Promise.resolve();
  if (remoteAreas.has(outletId)) return Promise.resolve();
  const pending = inflight.get(outletId);
  if (pending) return pending;

  const base = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;
  if (!base) return Promise.resolve();

  const job = fetch(`${base}/v3/buyer/outlet/${encodeURIComponent(outletId)}/service-area`)
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      if (!json?.data) return;
      remoteAreas.set(outletId, serviceAreaFromApi(json.data));
      areasVersion += 1;
      window.dispatchEvent(new Event(SERVICE_AREA_LOADED));
    })
    .catch(() => { /* keep the table's answer */ })
    .finally(() => { inflight.delete(outletId); });
  inflight.set(outletId, job);
  return job;
}

export function getServiceArea(outletId?: string | null): ServiceArea | null {
  if (!outletId) return null;
  if (remoteAreas.has(outletId)) return remoteAreas.get(outletId) ?? null;
  return SERVICE_AREAS[outletId] ?? null;
}

/** Normalizes "Bengaluru Rural" / " bangalore " to a comparable form. */
function normalizeCity(city: string): string {
  return city.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * `location` is the blob built by {@link addressLocationText}. Either signal is
 * enough: a serviceable pincode, or a serviceable city/locality name. Both are
 * individually unreliable — "Bangalore - 560100" carries the city but some
 * records only have the pincode, others only the locality — so we accept a
 * match from whichever one is present.
 */
export function isCityServiceable(area: ServiceArea, location?: string | null, coords?: LatLng | null): boolean {
  if (area.radius) {
    if (!location && !coords) return true; // no address chosen yet
    if (!area.radius.center) return true; // the store has no position on record
    if (!coords) return false; // an address we can't place can't be checked out either
    return haversineKm(area.radius.center, coords) <= area.radius.maxKm;
  }

  if (!location) return true; // no address chosen yet — decide at checkout, not here
  const normalized = normalizeCity(location);

  const pincode = Number(normalized.match(/\b(\d{6})\b/)?.[1]);
  if (pincode && area.pincodeRanges.some(([from, to]) => pincode >= from && pincode <= to)) return true;

  return area.cities.some((c) => normalized === c || normalized.includes(c));
}

/**
 * Location text we match against. The pincode is what actually decides; city,
 * locality and state are the fallback for records that have no pincode.
 */
export function addressLocationText(addr: {
  city?: string; sublocality?: string | null; state?: string; pincode?: string;
} | null | undefined): string | null {
  if (!addr) return null;
  const text = [addr.pincode, addr.city, addr.sublocality, addr.state].filter(Boolean).join(", ");
  return text || null;
}

/** City of the address the buyer currently has selected, if any. */
export function getSelectedCity(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(SELECTED_CITY_KEY);
}

export function setSelectedCity(city?: string | null) {
  if (typeof window === "undefined") return;
  if (city) localStorage.setItem(SELECTED_CITY_KEY, city);
  else localStorage.removeItem(SELECTED_CITY_KEY);
}

/**
 * True when this outlet restricts delivery and the selected address is outside
 * the serviceable cities.
 */
export function isDeliveryBlocked(
  outletId?: string | null,
  city = getSelectedCity(),
  coords = parseCoords(getSelectedCoordsRaw()),
): boolean {
  const area = getServiceArea(outletId);
  return !!area && !isCityServiceable(area, city, coords);
}

/* ── Selected-address coordinates ──────────────────────────────────────────
 * Mirrored next to the city for the same reason: so anything that needs to
 * reason about where the buyer is (delivery-distance labels on product cards,
 * the add-to-cart gate) can read it synchronously instead of refetching the
 * address book. The Header owns the writes; everyone else reads.
 */

export const SELECTED_COORDS_KEY = "selected_address_coords";

export interface LatLng { lat: number; lng: number }

/**
 * Raw stored JSON, not the parsed object.
 *
 * `useSyncExternalStore` requires getSnapshot to return a referentially stable
 * value — parsing here would hand React a fresh object every call and spin it
 * into an infinite re-render. Callers parse the string themselves, memoized.
 */
export function getSelectedCoordsRaw(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(SELECTED_COORDS_KEY);
}

export function parseCoords(raw: string | null): LatLng | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

/**
 * Always call this on address change, including with a coordinate-less address
 * — passing null CLEARS the mirror. Skipping the call on a missing coordinate
 * would leave the previous address's coordinates in place and label the new
 * one with the old address's distance.
 */
export function setSelectedCoords(coords?: LatLng | null) {
  if (typeof window === "undefined") return;
  if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
    localStorage.setItem(SELECTED_COORDS_KEY, JSON.stringify({ lat: coords.lat, lng: coords.lng }));
  } else {
    localStorage.removeItem(SELECTED_COORDS_KEY);
  }
}
